"use client";

import React from "react";
import { ArrowRight, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function HeavyHittersConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="flex gap-6 overflow-hidden pb-6">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="h-[420px] w-[300px] shrink-0 animate-pulse rounded-xl bg-muted/10" />)}
      </div>;
  }
  if (error) {
    return <div className="flex h-64 w-full flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/5 text-center">
        <p className="mb-4 text-muted-foreground">
          Unable to load the heavy hitters.
        </p>
        <Button onClick={refetch} variant="outline">
          Retry Loading
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="py-12 text-center text-muted-foreground">
        No products found in this collection.
      </div>;
  }
  return <ScrollArea className="w-full whitespace-nowrap pb-4">
      <div className="flex w-max space-x-6 pb-4">
        {products.map((product, i) => <div key={product.id} data-index={i} className="group relative w-[280px] shrink-0 md:w-[320px]">
            {}
            <div className="relative flex aspect-[3/4] flex-col justify-between overflow-hidden rounded-xl bg-muted/5 border border-border/40 transition-colors duration-500 hover:border-primary/30 hover:bg-muted/10">
              
              {}
              <div className="relative flex h-full w-full items-center justify-center p-6">
                {}
                <div className="absolute left-4 top-4 z-10">
                  <Badge variant="secondary" className="bg-background/80 text-xs font-bold uppercase tracking-wider text-foreground backdrop-blur-md border border-border/50">
                    <Zap className="mr-1 h-3 w-3 text-primary" />
                    Performance
                  </Badge>
                </div>

                <Link to={product.link} className="block h-full w-full">
                  {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-contain object-center transition-transform duration-500 group-hover:scale-105" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-muted/20 text-muted-foreground">
                      No Image
                    </div>}
                </Link>

                {}
                <div className="absolute bottom-4 right-4 translate-y-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                  <AddToCartButton productId={product.id} variant="default" size="sm" text="Quick Add" showQuantity={false} buttonClassName="bg-primary text-primary-foreground font-semibold shadow-[0_0_15px_rgba(var(--primary),0.4)] hover:bg-primary/90" className="w-auto" />
                </div>
              </div>

              {}
              <div className="border-t border-border/40 bg-card/40 p-4 backdrop-blur-sm">
                <Link to={product.link} className="block">
                  <h3 className="truncate text-base font-bold text-foreground transition-colors group-hover:text-primary">
                    {product.title}
                  </h3>
                </Link>
                <div className="mt-1 flex items-center justify-between">
                  <span className="font-mono text-sm font-medium text-muted-foreground">
                    {product.categories?.[0]?.name || "Running"}
                  </span>
                  <span className="font-mono text-lg font-bold text-foreground">
                    ${product.price?.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>)}
      </div>
      <ScrollBar orientation="horizontal" className="hidden md:flex" />
    </ScrollArea>;
}
export default function TheHeavyHitters() {
  const wp_query_params = {
    per_page: 8,
    orderby: "date",
    order: "desc",
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="the-heavy-hitters" className="dark bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        {}
        <div className="mb-10 flex flex-col items-start justify-between gap-6 md:mb-14 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <h2 className="text-3xl font-black uppercase tracking-tighter text-foreground md:text-5xl lg:text-6xl">
              The Heavy <span className="text-primary">Hitters</span>
            </h2>
            <p className="mt-4 text-lg text-muted-foreground md:text-xl">
              Top-tier performance models engineered for those who refuse to compromise. 
              Experience the pinnacle of athletic technology.
            </p>
          </div>
          <div className="shrink-0">
            <Link to="/limited-drops">
              <Button variant="outline" className="group border-primary/20 hover:border-primary hover:bg-primary/10">
                View All Collection
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>

        {}
        <EcommerceProductsProvider wp_query={{
        per_page: 8,
        orderby: "date",
        order: "desc",
        status: ["publish"]
      }} className="w-full">
          <HeavyHittersConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}